import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { shopify } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";
import { t } from "@/lib/i18n";
import { optimizeImg, srcSet } from "@/lib/img";

// Fixed order for the 3 core category tiles on the homepage.
const PREFERRED_CATEGORIES = [
  "marcini-hoodies-unisex",
  "marcini-t-shirts-unisex",
  "marcini-capses-unisex",
];

export default function Home() {
  const { data: hero } = useQuery({
    queryKey: ["homepage-hero"],
    queryFn: () => shopify.homepageHero(),
    staleTime: 60_000,
  });
  const { data: editorialProduct } = useQuery({
    queryKey: ["editorial-product"],
    queryFn: () => shopify.product("blue-haze-marcini-hoodie-unisex", "NO"),
    staleTime: 60_000,
  });
  const { data: nyeProdukter } = useQuery({
    queryKey: ["nye-produkter"],
    queryFn: () => shopify.collection("nye-produkter", "NO"),
  });
  const { data: allProducts = [] } = useQuery({
    queryKey: ["home-products"],
    queryFn: () => shopify.products("NO"),
  });
  const { data: collections = [] } = useQuery({
    queryKey: ["home-collections"],
    queryFn: () => shopify.collections("NO"),
  });

  // Always show a clean 4-column row on desktop / 2 on mobile. Any extra
  // products in the Nye Produkter collection are moved to the shop link CTA.
  const featured = (nyeProdukter?.products || allProducts).slice(0, 4);

  // Pick real Marcini editorial imagery from the actual catalog so no Unsplash placeholders remain.
  // We prefer the second image of each product (the full-body model shot).
  const editorialImages = useMemo(() => {
    const seen = new Set();
    const pool = [];
    for (const p of allProducts) {
      for (const img of p.images) {
        if (!seen.has(img)) {
          seen.add(img);
          pool.push({ src: img, title: p.color_name || p.title, handle: p.handle });
        }
      }
    }
    return pool;
  }, [allProducts]);

  // Custom brand hero: graffiti wall model shot (Marcini FW26 campaign).
  // Overrides any Shopify metaobject video/image for the moment — swap this
  // constant when you're ready for a new campaign asset (or wire back through
  // the Shopify Metaobject `homepage_hero` when you upload it there).
  const CUSTOM_HERO_IMAGE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-marcini/artifacts/i8b0rksc_DSC02727.webp";
  const heroImage = CUSTOM_HERO_IMAGE;
  const heroVideo = null;
  // Editorial split ONLY uses the Blue Haze group model shot (both models in navy hoodies).
  // No fallback — if the model shot isn't loaded yet, the section stays hidden.
  const editorialSplitImage = editorialProduct?.images?.[1];
  const heroEyebrow = hero?.eyebrow || t.home.eyebrow;
  const heroCtaLabel = hero?.cta_label || t.home.heroCta;
  const heroCtaLink = hero?.cta_link || "/shop";

  const { scrollY } = useScroll();
  const heroTitleY = useTransform(scrollY, [0, 500], [0, -60]);
  const heroTitleOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  // Curate the 3 core category tiles in a fixed order: Hoodies → T-skjorter → Caps.
  // Skips "frontpage" and "nye-produkter" (Nye is already featured in its own section above).
  const featuredCollections = useMemo(() => {
    const byHandle = Object.fromEntries(collections.map(c => [c.handle, c]));
    const used = new Set();
    return PREFERRED_CATEGORIES
      .map(h => byHandle[h])
      .filter(Boolean)
      .map(c => {
        const productsInCol = allProducts.filter(p => p.collections?.includes(c.handle));
        let cover = c.cover;
        if (cover && used.has(cover)) {
          const alt = productsInCol.find(p => p.images?.[0] && !used.has(p.images[0]));
          if (alt) cover = alt.images[0];
          else {
            const alt2 = productsInCol.find(p => p.images?.[1] && !used.has(p.images[1]));
            if (alt2) cover = alt2.images[1];
          }
        }
        if (cover) used.add(cover);
        return { ...c, cover };
      });
  }, [collections, allProducts]);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[100svh] w-full overflow-hidden bg-[color:var(--marcini-bg-warm)]" data-testid="hero-section">
        {heroVideo ? (
          <video
            key={heroVideo}
            src={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={(e) => e.currentTarget.classList.remove("opacity-0")}
            className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500"
            data-testid="hero-video"
          />
        ) : heroImage ? (
          <img
            src={optimizeImg(heroImage, 2000, { hq: true })}
            srcSet={srcSet(heroImage, [1000, 1400, 2000, 2400], { hq: true })}
            sizes="100vw"
            alt=""
            aria-hidden
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-95"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/70 md:from-white/20 md:to-white/60" />

        <motion.div
          style={{ y: heroTitleY, opacity: heroTitleOpacity }}
          className="relative z-10 h-full flex flex-col justify-end px-5 md:px-12 pb-14 md:pb-24"
        >
          <h1 className="leading-[0.85] tracking-tight" aria-label="Marcini">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_premium-marcini/artifacts/12zfnghs_ChatGPT%20Image%2030.%20aug.%202026%2C%2010_55_37.png"
              alt="Marcini"
              className="w-[75vw] md:w-[42vw] max-w-[820px] h-auto object-contain drop-shadow-[0_6px_30px_rgba(0,0,0,0.25)]"
              fetchPriority="high"
            />
          </h1>
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-5 md:gap-6">
            <Link
              to={heroCtaLink}
              data-testid="hero-cta-shop"
              className="inline-flex items-center gap-3 border border-black px-6 py-3 text-eyebrow bg-white/80 hover:bg-black hover:text-white transition-colors"
            >
              {heroCtaLabel} <ArrowRight size={14} />
            </Link>
            <Link to="/lookbook" data-testid="hero-cta-lookbook" className="link-underline text-eyebrow">{t.home.heroSecondary}</Link>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE / EDITORIAL BAND */}
      <section className="border-y border-[color:var(--marcini-border)] overflow-hidden bg-white">
        <div className="flex whitespace-nowrap marquee-track py-4 md:py-5 font-serif-editorial text-2xl md:text-4xl">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-10 md:gap-14 pr-10 md:pr-14 items-center">
              <span>Norsk design</span><span className="text-[color:var(--marcini-fg-3)]">·</span>
              <span>Håndlagde detaljer</span><span className="text-[color:var(--marcini-fg-3)]">·</span>
              <span>Rask levering — 3–7 virkedager</span><span className="text-[color:var(--marcini-fg-3)]">·</span>
              <span>Begrenset antall</span><span className="text-[color:var(--marcini-fg-3)]">·</span>
              <span>Sendes fra Oslo</span><span className="text-[color:var(--marcini-fg-3)]">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCT ROWS BY CATEGORY — all products visible on the front page */}
      {[
        { handle: "marcini-capses-unisex", title: "Caps", eyebrow: "Kolleksjon 01" },
        { handle: "marcini-t-shirts-unisex", title: "T-skjorter", eyebrow: "Kolleksjon 02" },
        { handle: "marcini-hoodies-unisex", title: "Hoodies", eyebrow: "Kolleksjon 03" },
      ].map(cat => {
        const items = allProducts.filter(p => p.collections?.includes(cat.handle));
        if (items.length === 0) return null;
        return (
          <section
            key={cat.handle}
            className="px-5 md:px-12 py-16 md:py-24"
            data-testid={`row-${cat.handle}`}
          >
            <div className="flex items-end justify-between gap-4 mb-8 md:mb-12">
              <div>
                <p className="text-eyebrow mb-2">{cat.eyebrow}</p>
                <h2 className="font-serif-editorial text-4xl md:text-6xl leading-[0.9]">{cat.title}</h2>
              </div>
              <Link
                to={`/collections/${cat.handle}`}
                data-testid={`row-${cat.handle}-viewall`}
                className="link-underline text-eyebrow whitespace-nowrap"
              >
                Se alle →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8">
              {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        );
      })}

      {/* EDITORIAL SPLIT */}
      {editorialSplitImage && (
        <section className="grid md:grid-cols-2 min-h-[70vh] md:min-h-[80vh]">
          <div className="relative overflow-hidden aspect-[4/5] md:aspect-auto">
            <img
              src={optimizeImg(editorialSplitImage, 1200)}
              srcSet={srcSet(editorialSplitImage, [600, 1000, 1400])}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt="Editorial"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col justify-center px-6 md:px-20 py-16 md:py-20 bg-[color:var(--marcini-bg-warm)]">
            <h3 className="font-serif-editorial text-4xl md:text-6xl leading-[0.95] mb-6 md:mb-8">
              {t.home.editorialTitle}
            </h3>
            <p className="max-w-md text-[color:var(--marcini-fg-2)] leading-relaxed mb-8 md:mb-10">
              {t.home.editorialBody}
            </p>
            <Link to="/lookbook" data-testid="editorial-cta" className="inline-flex items-center gap-3 text-eyebrow link-underline">
              {t.home.editorialCta} <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* LOOKBOOK PREVIEW — from real editorial images, dedup */}
      {editorialImages.length >= 4 && (
        <section className="px-5 md:px-12 pb-20 md:pb-32">
          <div className="flex items-end justify-between mb-8 md:mb-10">
            <h2 className="font-serif-editorial text-4xl md:text-6xl">{t.home.lookbookTitle}</h2>
            <Link to="/lookbook" className="link-underline text-eyebrow" data-testid="lookbook-view-all">{t.home.lookbookViewAll}</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {editorialImages.slice(2, 6).map((im, i) => (
              <Link to={`/products/${im.handle}`} key={im.src} className={`relative overflow-hidden group ${i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[3/4]"}`}>
                <img src={optimizeImg(im.src, 800)} srcSet={srcSet(im.src, [400, 800, 1200])} sizes="(min-width: 768px) 25vw, 50vw" alt={im.title} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]" loading="lazy" />
                <div className="absolute bottom-3 left-3 text-white font-serif-editorial text-lg md:text-xl drop-shadow-md">{im.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
